/** Small presentational pieces shared by the pages. */
import { ActionIcon, CopyButton, Group, Text, Tooltip } from "@mantine/core";

import { IconCheck, IconCopy } from "./icons";

type Tone = "ok" | "warning" | "alarm" | "off";
const DOT: Record<Tone, string> = {
  ok: "var(--mantine-color-teal-5)",
  warning: "var(--mantine-color-yellow-5)",
  alarm: "var(--mantine-color-red-6)",
  off: "var(--mantine-color-gray-5)",
};

/** A status dot with its meaning for screen readers. */
export function StatusDot({ tone, label, size = 8 }: { tone: Tone; label: string; size?: number }) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: DOT[tone],
        boxShadow: tone === "off" ? undefined : `0 0 0 3px color-mix(in srgb, ${DOT[tone]} 22%, transparent)`,
        flex: "none",
      }}
    />
  );
}

/** An id shortened to `chars` characters, with a copy button for the whole of it. */
export function CopyId({ value, chars = 8 }: { value: string; chars?: number }) {
  return (
    <Group gap={4} wrap="nowrap">
      <Text ff="monospace" size="xs">
        {value.length > chars ? `${value.slice(0, chars)}…` : value}
      </Text>
      <CopyButton value={value} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : "Copy"}>
            <ActionIcon size="xs" variant="subtle" color={copied ? "teal" : "gray"} onClick={copy} aria-label="Copy">
              {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

/** Colour chips with labels (a legend). */
export function LegendChips({ items }: { items: { label: string; colour: string; shape?: "box" | "line" }[] }) {
  return (
    <Group gap="md" wrap="wrap">
      {items.map((it) => (
        <Group key={it.label} gap={6} wrap="nowrap">
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: it.shape === "line" ? 14 : 10,
              height: it.shape === "line" ? 0 : 10,
              borderRadius: it.shape === "line" ? 0 : 3,
              background: it.shape === "line" ? undefined : it.colour,
              borderTop: it.shape === "line" ? `2px dashed ${it.colour}` : undefined,
            }}
          />
          <Text size="xs" c="dimmed">
            {it.label}
          </Text>
        </Group>
      ))}
    </Group>
  );
}

/** A slim horizontal bar: `frac` of the width (decorative; the number is beside it). */
export function MiniBar({ frac, colour, width = 64 }: { frac: number; colour: string; width?: number | string }) {
  const f = Math.max(0, Math.min(1, Number.isFinite(frac) ? frac : 0));
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width,
        height: 4,
        borderRadius: 2,
        background: "var(--mantine-color-gray-2)",
        overflow: "hidden",
        verticalAlign: "middle",
      }}
    >
      <span style={{ display: "block", width: `${f * 100}%`, height: "100%", background: colour, borderRadius: 2 }} />
    </span>
  );
}
